import { useState } from 'react';

import { Pencil, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { ConfirmationModal } from '@/modules/common/components/modals/confirmation-modal';
import { usePermissions } from '@/modules/common/hooks/use-permissions';
import { OpsiumPolicy } from '@/modules/common/types/opsium-policy';
import { formatCompactDateRange } from '@/utils/date-formatters';

import { useCancelTimeOff } from '../../../hooks/use-cancel-time-off';
import { TimeOffRequest, TimeOffRequestAllowedAction } from '../../../types/time-off-request';

interface TimeOffActionsCellProps {
  request: TimeOffRequest;
  onEditRequest: (request: TimeOffRequest) => void;
}

export const TimeOffActionsCell = ({ request, onEditRequest }: TimeOffActionsCellProps) => {
  const { t } = useTranslation('authenticated.timeOff');
  const { hasAnyPermission } = usePermissions();
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const cancelMutation = useCancelTimeOff({
    onSuccess: () => setIsCancelModalOpen(false),
  });

  const canCancelPolicy = hasAnyPermission([OpsiumPolicy.TimeOffsCancel]);
  const canCancelRequest = request.allowedActions.includes(TimeOffRequestAllowedAction.CANCEL);
  const canEditRequest = request.allowedActions.includes(TimeOffRequestAllowedAction.EDIT);

  if (!canEditRequest && (!canCancelRequest || !canCancelPolicy)) {
    return null;
  }

  return (
    // Stop propagation so action buttons never trigger the row's navigation click.
    <div onClick={event => event.stopPropagation()} className="flex items-center gap-1">
      {canEditRequest && (
        <ConfirmationModal
          open={isEditModalOpen}
          onOpen={() => setIsEditModalOpen(true)}
          onClose={() => setIsEditModalOpen(false)}
          title={t('editModal.title')}
          message={t('editModal.message')}
          confirmText={t('editModal.confirm')}
          cancelText={t('editModal.cancel')}
          confirmVariant="default"
          onConfirm={closeModal => {
            closeModal();
            onEditRequest?.(request);
          }}
          trigger={
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-muted-foreground hover:bg-muted hover:text-foreground"
              aria-label={t('actions.editTimeOff')}
            >
              <Pencil className="h-4 w-4" />
            </Button>
          }
        />
      )}

      {canCancelRequest && canCancelPolicy && (
        <ConfirmationModal
          open={isCancelModalOpen}
          onOpen={() => setIsCancelModalOpen(true)}
          onClose={() => setIsCancelModalOpen(false)}
          title={t('cancelModal.title')}
          message={t('cancelModal.message', {
            type: request.type.displayName,
            dateRange: formatCompactDateRange(request.startDate, request.endDate),
          })}
          confirmText={t('cancelModal.confirm')}
          cancelText={t('cancelModal.cancel')}
          confirmVariant="destructive"
          isLoading={cancelMutation.isPending}
          onConfirm={async () => {
            await cancelMutation.mutateAsync(request.id);
          }}
          trigger={
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-destructive hover:bg-destructive/10 hover:text-destructive"
              aria-label={t('actions.cancelTimeOff')}
            >
              <X className="h-4 w-4" />
            </Button>
          }
        />
      )}
    </div>
  );
};
